import { Printer, ArrowLeft } from 'lucide-react'
import {
  TrendingDown,
  ShieldCheck,
  CalendarClock,
  ArrowRightLeft,
  Sparkles,
} from 'lucide-react'
import { CHALLENGES } from '@/data/modules'
import type { useProgress } from '@/hooks/useProgress'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import type { PageView } from '@/types'

const ICON_MAP: Record<string, React.ReactNode> = {
  TrendingDown: <TrendingDown size={16} />,
  ShieldCheck: <ShieldCheck size={16} />,
  CalendarClock: <CalendarClock size={16} />,
  ArrowRightLeft: <ArrowRightLeft size={16} />,
  Sparkles: <Sparkles size={16} />,
}

interface QuickReferenceProps {
  modules: import('@/types').Module[]
  progress: ReturnType<typeof useProgress>
  onNavigate: (view: PageView) => void
}

export function QuickReference({ modules, progress, onNavigate }: QuickReferenceProps) {

  return (
    <div className="space-y-8 animate-slide-up print-full">
      {/* Header */}
      <div className="flex items-center justify-between no-print">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Quick Reference</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            All 5 selling methods at a glance — print or share with your team.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => window.print()}>
            <Printer size={14} />
            Print
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onNavigate({ page: 'dashboard' })}>
            <ArrowLeft size={14} />
            Back
          </Button>
        </div>
      </div>

      {/* Print header (hidden on screen) */}
      <div className="hidden print:block mb-6">
        <h1 className="text-2xl font-bold">365 Hound — Partner Selling Methods</h1>
        <p className="text-sm text-gray-500">Documented from Andrew Austen's Pax8 Train-the-Trainer session, 1 April 2026</p>
      </div>

      {/* Methods table */}
      <section>
        <h2 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-4">
          The 5 methods
        </h2>
        <div className="space-y-4">
          {modules.map(mod => (
            <div
              key={mod.id}
              className="rounded-xl border border-[var(--border)] bg-[var(--bg-primary)] overflow-hidden"
            >
              {/* Method header */}
              <div
                className="flex items-center gap-3 px-4 py-3 bg-[var(--bg-secondary)] border-b border-[var(--border)] cursor-pointer no-print"
                onClick={() => onNavigate({ page: 'module', moduleId: mod.id })}
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[var(--accent-blue-light)] flex items-center justify-center text-[var(--accent-blue)]">
                  {ICON_MAP[mod.icon]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[var(--text-muted)]">Method {mod.number}</span>
                    {progress.isComplete(mod.id) && <Badge variant="green">✓ Done</Badge>}
                  </div>
                  <h3 className="font-semibold text-[var(--text-primary)] text-sm">{mod.title}</h3>
                </div>
                <span className="text-xs text-[var(--text-muted)]">{mod.estimatedMinutes} min</span>
              </div>

              {/* Print-only header */}
              <div className="hidden print:flex items-center gap-3 px-4 py-3 bg-gray-50 border-b">
                <span className="font-bold text-sm">Method {mod.number}: {mod.title}</span>
              </div>

              <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                {/* Tagline */}
                <div>
                  <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-1">
                    The pitch
                  </p>
                  <p className="text-[var(--text-secondary)]">{mod.tagline}</p>
                </div>

                {/* Steps summary */}
                <div>
                  <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-1">
                    Key steps
                  </p>
                  <ol className="space-y-1">
                    {mod.steps.map(step => (
                      <li key={step.number} className="flex items-start gap-1.5 text-[var(--text-secondary)]">
                        <span className="flex-shrink-0 w-4 h-4 rounded-full bg-[var(--bg-tertiary)] flex items-center justify-center text-[10px] font-bold text-[var(--text-muted)]">
                          {step.number}
                        </span>
                        <span className="leading-tight">{step.title}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Top talking point */}
                <div>
                  <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-1">
                    Lead with
                  </p>
                  <p className="text-[var(--text-secondary)] italic">
                    {mod.keyTalkingPoints[0]}
                  </p>
                  {mod.pricingNote && (
                    <p className="mt-2 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                      💰 {mod.pricingNote}
                    </p>
                  )}
                  {mod.realWorldExample && (
                    <p className="mt-2 text-xs text-[var(--accent-orange)] font-medium">
                      Example: {mod.realWorldExample.outcome}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 6 challenges */}
      <section>
        <h2 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-3">
          6 core customer challenges
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {CHALLENGES.map((c, i) => {
            const [title] = c.split(' — ')
            return (
              <div key={i} className="flex items-center gap-2.5 text-sm">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[var(--accent-blue-light)] flex items-center justify-center text-[10px] font-bold text-[var(--accent-blue)]">
                  {i + 1}
                </span>
                <span className="text-[var(--text-secondary)]">{title}</span>
              </div>
            )
          })}
        </div>
      </section>


      {/* Key takeaway */}
      <section>
        <div className="rounded-xl bg-gradient-to-br from-[var(--accent-blue)] to-[#1e7db8] text-white p-5">
          <p className="text-xs font-semibold uppercase tracking-wide opacity-80 mb-2">Key takeaway</p>
          <p className="text-sm leading-relaxed">
            Lead with <strong>subscription optimisation</strong> (immediate, tangible savings), then layer in <strong>security uplift</strong> and <strong>roadmapping</strong> as ongoing professional services. The tool works for two personas: the partner driving services revenue, and the self-sufficient enterprise customer managing their own environment.
          </p>
        </div>
      </section>
    </div>
  )
}
